/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

* This template has been built upon, with additional code and configuration by Tazi Rides Ltd.
*/

/**
  This file is used for controlling the global states of the components,
  you can customize the states for the different components here.
*/

import React, { createContext, ReactNode, useContext, useMemo, useReducer } from 'react'

import { User } from 'firebase/auth'

import { routes, RouteType } from 'routes'
import { OrganisationMembers } from 'types/server/user-details'

// Define the context value type
type MaterialUIContextType = [StateTypes, React.Dispatch<ActionTypes>]

// The Material Dashboard 2 PRO React TSUI Dashboard PRO Material main context
const MaterialUI = createContext<MaterialUIContextType>(null)

// Setting custom name for the context which is visible on react dev tools
MaterialUI.displayName = 'MaterialUIContext'

export type UserDataRole = 'ADMIN' | 'EDITOR' | 'VIEWER'

interface OrganisationData {
	id: string
	logo: string
	name: string
	members?: OrganisationMembers[]
	default_location: {
		geometry: {
			lat: number
			lng: number
		}
		name: string
	}
}

interface UserData {
	role: UserDataRole
	organisation: OrganisationData
}

interface StateTypes {
	session: {
		user: User
		data: UserData
	} | null
	miniSidenav: boolean
	transparentSidenav: boolean
	whiteSidenav: boolean
	sidenavColor:
		| 'primary'
		| 'secondary'
		| 'info'
		| 'success'
		| 'warning'
		| 'error'
		| 'light'
		| 'dark'
	transparentNavbar: boolean
	fixedNavbar: boolean
	layout: 'dashboard' | 'page'
	darkMode: boolean
	routes: RouteType[]
}

interface ActionTypes {
	type:
		| 'MINI_SIDENAV'
		| 'TRANSPARENT_SIDENAV'
		| 'WHITE_SIDENAV'
		| 'SIDENAV_COLOR'
		| 'TRANSPARENT_NAVBAR'
		| 'FIXED_NAVBAR'
		| 'LAYOUT'
		| 'DARKMODE'
		| 'SESSION'
		| 'ROUTES'
	value: any
}

// Material Dashboard 2 PRO React reducer
function reducer(state: StateTypes, action: ActionTypes) {
	switch (action.type) {
		case 'MINI_SIDENAV': {
			return { ...state, miniSidenav: action.value }
		}
		case 'TRANSPARENT_SIDENAV': {
			return { ...state, transparentSidenav: action.value }
		}
		case 'WHITE_SIDENAV': {
			return { ...state, whiteSidenav: action.value }
		}
		case 'SIDENAV_COLOR': {
			return { ...state, sidenavColor: action.value }
		}
		case 'TRANSPARENT_NAVBAR': {
			return { ...state, transparentNavbar: action.value }
		}
		case 'FIXED_NAVBAR': {
			return { ...state, fixedNavbar: action.value }
		}
		case 'LAYOUT': {
			return { ...state, layout: action.value }
		}
		case 'DARKMODE': {
			return { ...state, darkMode: action.value }
		}
		case 'SESSION': {
			return { ...state, session: action.value }
		}
		case 'ROUTES': {
			return { ...state, routes: action.value }
		}
		default: {
			throw new Error(`Unhandled action type: ${action.type}`)
		}
	}
}

// Material Dashboard 2 PRO React context provider
function MaterialUIControllerProvider({ children }: { children: ReactNode }): JSX.Element {
	const initialState: StateTypes = {
		session: null,
		miniSidenav: false,
		transparentSidenav: false, //TODO: Don't think we need but need deep integrated
		whiteSidenav: false, //TODO: Don't think we need but need deep integrated
		sidenavColor: 'primary',
		transparentNavbar: true,
		fixedNavbar: false,
		layout: 'dashboard',
		darkMode: false,
		routes: routes,
	}

	const [controller, dispatch] = useReducer(reducer, initialState)

	const value = useMemo(
		() => [controller, dispatch] as MaterialUIContextType,
		[controller, dispatch]
	)

	return <MaterialUI.Provider value={value}>{children}</MaterialUI.Provider>
}

// Material Dashboard 2 PRO React custom hook for using context
function useMaterialUIController() {
	const context = useContext(MaterialUI)

	if (!context) {
		throw new Error(
			'useMaterialUIController should be used inside the MaterialUIControllerProvider.'
		)
	}

	return context
}

// Context module functions
const setMiniSidenav = (
	dispatch: (arg: { type: 'MINI_SIDENAV'; value: boolean }) => void,
	value: boolean
) => dispatch({ type: 'MINI_SIDENAV', value })

const setTransparentSidenav = (
	dispatch: (arg: { type: 'TRANSPARENT_SIDENAV'; value: boolean }) => void,
	value: boolean
) => dispatch({ type: 'TRANSPARENT_SIDENAV', value })

const setWhiteSidenav = (
	dispatch: (arg: { type: 'WHITE_SIDENAV'; value: boolean }) => void,
	value: boolean
) => dispatch({ type: 'WHITE_SIDENAV', value })

const setTransparentNavbar = (
	dispatch: (arg: { type: 'TRANSPARENT_NAVBAR'; value: boolean }) => void,
	value: boolean
) => dispatch({ type: 'TRANSPARENT_NAVBAR', value })

const setFixedNavbar = (
	dispatch: (arg: { type: 'FIXED_NAVBAR'; value: boolean }) => void,
	value: boolean
) => dispatch({ type: 'FIXED_NAVBAR', value })

const setOpenConfigurator = (
	dispatch: (arg: { type: 'OPEN_CONFIGURATOR'; value: boolean }) => void,
	value: boolean
) => dispatch({ type: 'OPEN_CONFIGURATOR', value })

const setLayout = (
	dispatch: (arg: { type: 'LAYOUT'; value: 'dashboard' | 'page' }) => void,
	value: 'dashboard' | 'page'
) => dispatch({ type: 'LAYOUT', value })

const setDarkMode = (
	dispatch: (arg: { type: 'DARKMODE'; value: boolean }) => void,
	value: boolean
) => dispatch({ type: 'DARKMODE', value })

const setUser = (
	dispatch: (arg: { type: 'SESSION'; value: { user: User; data: UserData } | null }) => void,
	value: { user: User; data: UserData } | null
) => dispatch({ type: 'SESSION', value })

const updateRoutes = (
	dispatch: (arg: { type: 'ROUTES'; value: RouteType[] }) => void,
	routes: RouteType[],
	newRoutes: RouteType[]
) => {
	const parsedRoutes = newRoutes
		.map(nR => {
			if (routes.some(r => r.key === nR.key)) {
				return null // Skip if route already exists
			} else {
				return nR
			}
		})
		.filter(nR => nR !== null)

	return dispatch({ type: 'ROUTES', value: routes.concat(parsedRoutes) })
}

export {
	MaterialUIControllerProvider,
	useMaterialUIController,
	setMiniSidenav,
	setTransparentSidenav,
	setWhiteSidenav,
	setTransparentNavbar,
	setFixedNavbar,
	setOpenConfigurator,
	setLayout,
	setDarkMode,
	setUser,
	updateRoutes,
}
